import { useConvertToModernPagination } from '@/context/services/hooks/usePagination';
import {
  isAuthError,
  isForbiddenError,
  isSuccess,
} from '@/context/services/type';
import { handleInvoiceReportForbiddenStatusCode } from '@/context/services/invoice_reports/handleInvoiceReportForbiddenStatusCode';
import { useResponseToInvoiceReportColumn } from '@/features/invoice_reports/components/List/hooks';
import type {
  FetchInvoiceReportsArgs,
  InvoiceReportColumn,
} from '@/features/invoice_reports/types';
import { ModernPaginationProps } from '@moneyforward/ap-frontend-components';
import {
  CommonError,
  InvoiceReportPaymentInfoResponse,
  useGetInvoiceReportPaymentInfoSuspense,
  useGetInvoiceReportPaymentStatusesSuspense,
  useSearchInvoiceReports,
} from 'ap-openapi';
import { useMemo } from 'react';

const isCommonError = (value: unknown): value is CommonError =>
  typeof value === 'object' && value !== null;

export function useInvoiceReportsSearch(args: FetchInvoiceReportsArgs = {}) {
  const { page = 1, perPage = 50, query, order_by } = args;

  const { data, refetch, isFetching } = useSearchInvoiceReports(
    {
      pagination: {
        per_page: perPage,
        page,
      },
      query,
      order_by: order_by
        ? order_by.map((item) => ({
            field: item.field,
            direction: item.direction,
          }))
        : undefined,
    },
    undefined,
    {
      query: {
        queryKey: ['invoiceReports', { page, perPage, query, order_by }],
      },
    }
  );

  const pagination: ModernPaginationProps = useConvertToModernPagination(
    data?.data && typeof data.data === 'object' && 'pagination' in data.data
      ? data.data.pagination
      : undefined
  );

  const result: InvoiceReportColumn[] = useMemo(
    () =>
      data?.data &&
      typeof data.data === 'object' &&
      'invoice_reports' in data.data
        ? data.data.invoice_reports?.map(
            useResponseToInvoiceReportColumn<InvoiceReportColumn>
          ) ?? []
        : [],
    [data]
  );

  if (!isSuccess(data?.status)) {
    handleInvoiceReportForbiddenStatusCode(data?.status);
  }

  return {
    pagination,
    data: result,
    refetch,
    isFetching,
    isError:
      !isSuccess(data?.status) &&
      !isAuthError(data?.status) &&
      !isForbiddenError(data?.status),
    error: data && isCommonError(data.data) ? { ...data.data } : {},
  };
}

export const useGetInvoiceReportPaymentInfo = (
  reportId: string
): { data: InvoiceReportPaymentInfoResponse | undefined } => {
  const { data, error } = useGetInvoiceReportPaymentInfoSuspense(reportId);
  if (error) throw error;
  if (isSuccess(data?.status)) {
    return { data: data?.data };
  } else {
    return { data: undefined };
  }
};

export const useGetInvoiceReportsPaymentStatuses = () => {
  const { data, error, isLoading } = useGetInvoiceReportPaymentStatusesSuspense(
    {}
  );
  if (error) throw error;

  if (isSuccess(data?.status)) {
    return {
      data,
      isLoading,
    };
  } else {
    return {
      error: data && isCommonError(data.data) ? { ...data.data } : {},
      isLoading,
    };
  }
};
